/* eslint-disable max-lines -- Pre-existing violation */
/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
import {skipToken} from '@reduxjs/toolkit/query';
import {
  Alert,
  Card,
  DataStatus,
  Dialog,
  DialogFooter,
  Flag,
  Form,
  FormButton,
  FormField,
  Search,
  Segment,
  Table,
  TableRow,
  openConfirmDialog,
  useFormContext,
  type FormControl,
} from 'platform/components';
import {Grid, HStack, Show, Text, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useState} from 'react';
import {Controller, useWatch, type Control} from 'react-hook-form';

import {isNil} from 'ramda';

import {
  useArchiveFeatureMutation,
  useArchivePackageMutation,
  useCreateFeatureRuleMutation,
  useDeleteFeatureRuleMutation,
  useGetAuthorConfiguratorBundleQuery,
  useListFeatureQuery,
  useListFeatureRuleQuery,
  useListPackageQuery,
  useListTrimQuery,
  useUpdateFeatureMutation,
  useUpdateFeatureRuleMutation,
  type FeatureResponseBody,
  type FeatureRuleResponseBody,
  type PackageResponseBody,
} from '@dms/api/new-vehicle';
import {
  FEATURE_CATEGORIES,
  MoneyText,
  NewVehicleStatusFlag,
  getFeatureCategoryOptions,
  getNewVehicleEnumLabel,
} from '@dms/features/new-vehicle';
import i18n from '@dms/i18n';

import type {ApiError, Nullish} from 'shared/web';

import {
  actionsColumn,
  archiveRowAction,
  Cell,
  column,
  editRowAction,
  handleCatalogueError,
  joinParts,
  optionalText,
  RowActions,
  TabIntro,
  t,
} from './catalogueUi';
import {GrossPriceField} from './GrossPriceField';
import {optionalMoneyInput, priceInput} from './moneyInput';
import {NewCatalogueEquipment} from './NewCatalogueEquipment';
import {NewPricelistVersionDialog} from './NewPricelistVersionDialog';
import {NumberField} from './NumberField';
import {PackageBuilder} from './PackageBuilder';
import {PriceRow} from './PriceRow';
import {PriceWithGross} from './PriceWithGross';
import {ruleSentence} from './ruleSentence';
import {TrimEquipmentMatrix} from './TrimEquipmentMatrix';
import {useCatalogueVatRate} from './useCatalogueVatRate';
import {usePublishedPriceSave} from './usePublishedPriceSave';

const featureSchema = z.object({
  name: z.string().min(1),
  category: z.enum(FEATURE_CATEGORIES),
  kind: z.enum(['option', 'wheel', 'accessory', 'colour', 'upholstery']),
  fitStage: z.enum(['factory', 'dealer']),
  standaloneOrderable: z.boolean(),
  factoryCode: optionalText,
  defaultPriceNet: priceInput,
  costNet: optionalMoneyInput,
});
const ruleSchema = z.object({
  type: z.enum(['requires', 'excludes', 'autoAdd']),
  sourceFeatureId: z.string().min(1),
  targetFeatureIds: z.array(z.string()).min(1),
  ifSelectedFeatureIds: z.array(z.string()),
  note: optionalText,
});
type FeatureValues = z.infer<typeof featureSchema>;
export type RuleValues = z.infer<typeof ruleSchema>;
const emptyCondition = {anyOfVariantIds: [], allOfFeatureIds: []};
const conflictMessage = (error: unknown) => {
  if (typeof error !== 'object' || error === null) {
    return '';
  }
  const data = 'data' in error ? error.data : 'errors' in error ? error.errors : undefined; // eslint-disable-line no-nested-ternary -- Pre-existing violation
  if (typeof data !== 'object' || data === null) {
    return '';
  }
  if ('message' in data && typeof data.message === 'string') {
    return data.message;
  }
  if (!('errors' in data) || !Array.isArray(data.errors)) {
    return '';
  }
  const first = data.errors[0];
  return typeof first === 'object' &&
    first !== null &&
    'message' in first &&
    typeof first.message === 'string'
    ? first.message
    : '';
};

function FeatureKindAndCategoryFields({control}: {control: FormControl<FeatureValues>}) {
  const form = useFormContext<FeatureValues>();
  const kind = useWatch({control, name: 'kind'});
  return (
    <>
      <FormField
        control={control}
        name="category"
        type="choice"
        menuInPortal
        isNotClearable
        isDisabled={kind === 'wheel'}
        label={t('entity.newVehicle.catalogue.category', 'Category')}
        options={getFeatureCategoryOptions(t)}
      />
      <FormField
        control={control}
        name="kind"
        type="choice"
        menuInPortal
        label={t('entity.newVehicle.catalogue.kind', 'Kind')}
        options={['option', 'wheel', 'accessory', 'colour', 'upholstery'].map((value) => ({
          value,
          label: getNewVehicleEnumLabel('featureKind', value, t),
        }))}
        onChange={(value) => {
          // BE rejects a wheel set outside wheels_tyres (DMS-NV-CT-0038).
          if (value === 'wheel') {
            form.setValue('category', 'wheels_tyres', {shouldDirty: true});
          }
        }}
      />
    </>
  );
}

function FeatureDialog(props: {
  item: FeatureResponseBody;
  usedByModelYearId: string | undefined;
  canViewCost: boolean;
  vatRate: string | Nullish;
  done: () => void;
}) {
  const [update] = useUpdateFeatureMutation();
  const publishedPrice = usePublishedPriceSave(props.usedByModelYearId);
  const defaultValues: FeatureValues = {
    name: props.item.name,
    category: props.item.category,
    kind: props.item.kind,
    fitStage: props.item.fitStage,
    standaloneOrderable: props.item.standaloneOrderable,
    factoryCode: props.item.factoryCode ?? '',
    defaultPriceNet: props.item.defaultPriceNet,
    costNet: props.item.costNet ?? '',
  };
  const save = (values: FeatureValues) =>
    update({
      featureId: props.item.id,
      featureWriteRequestBody: {
        name: values.name,
        codes: props.item.codes,
        category: values.category,
        kind: values.kind,
        fitStage: values.fitStage,
        standaloneOrderable: values.standaloneOrderable,
        factoryCode: values.factoryCode || null,
        defaultPriceNet: values.defaultPriceNet,
        costNet: props.canViewCost ? values.costNet || null : props.item.costNet,
        version: props.item.version,
      },
    }).unwrap();
  const submit = async (values: FeatureValues) => {
    await publishedPrice
      .save({
        values,
        storedValues: defaultValues,
        priceField: 'defaultPriceNet',
        scope: 'feature',
        resourceId: props.item.id,
        save,
        onDone: props.done,
      })
      .catch(handleCatalogueError);
  };
  return (
    <>
      <Form<FeatureValues>
        experimentalZodSchema={featureSchema}
        defaultValues={defaultValues}
        onSubmit={submit}
      >
        {(control) => (
          <VStack spacing={4}>
            <Grid columns={2} spacing={3}>
              <FormField
                control={control}
                name="name"
                type="text"
                label={t('general.labels.name', 'Name')}
              />
              <FeatureKindAndCategoryFields control={control} />
              <FormField
                control={control}
                name="fitStage"
                type="choice"
                menuInPortal
                label={t('entity.newVehicle.catalogue.fitStage', 'Fit stage')}
                helperText={t(
                  'entity.newVehicle.catalogue.dealerFitHelp',
                  'Dealer-fit equipment is never locked with the factory specification.'
                )}
                options={['factory', 'dealer'].map((value) => ({
                  value,
                  label: getNewVehicleEnumLabel('fitStage', value, t),
                }))}
              />
              <FormField
                control={control}
                name="standaloneOrderable"
                type="checkbox"
                label={t('entity.newVehicle.catalogue.standaloneOrderable', 'Standalone orderable')}
              />
              <FormField
                control={control}
                name="factoryCode"
                type="text"
                label={t('entity.newVehicle.catalogue.factoryCode', 'Factory code')}
              />
              <PriceRow
                vatRate={props.vatRate}
                canViewCost={props.canViewCost}
                spacing={3}
                span={2}
              >
                <GrossPriceField
                  control={control}
                  name="defaultPriceNet"
                  vatRate={props.vatRate}
                  label={t('entity.newVehicle.catalogue.defaultPriceNet', 'Default price net')}
                />
                <Show when={props.canViewCost}>
                  <NumberField
                    control={control}
                    name="costNet"
                    label={t('entity.newVehicle.catalogue.costNet', 'Cost net')}
                  />
                </Show>
              </PriceRow>
            </Grid>
            <DialogFooter>
              <HStack justify="flex-end">
                <FormButton
                  control={control}
                  type="button"
                  title={t('general.actions.save', 'Save')}
                  onClick={() => void control.submitForm()}
                />
              </HStack>
            </DialogFooter>
          </VStack>
        )}
      </Form>
      <NewPricelistVersionDialog {...publishedPrice.dialogProps} />
    </>
  );
}

const matchesEquipmentSearch = (row: FeatureResponseBody, term: string) =>
  [row.name, row.factoryCode, getNewVehicleEnumLabel('featureCategory', row.category, t)].some(
    (value) => value?.toLocaleLowerCase().includes(term)
  );

export function FeaturesTab({
  modelYearId,
  currency,
  canWrite,
  canViewCost,
}: {
  modelYearId?: string;
  currency: string;
  canWrite: boolean;
  canViewCost: boolean;
}) {
  const query = useListFeatureQuery({});
  const bundle = useGetAuthorConfiguratorBundleQuery(modelYearId ? {modelYearId} : skipToken);
  const packages = useListPackageQuery({});
  const rules = useListFeatureRuleQuery({});
  const [archive] = useArchiveFeatureMutation();
  const vatRate = useCatalogueVatRate(modelYearId);
  const [editing, setEditing] = useState<FeatureResponseBody | 'new'>();
  const [references, setReferences] = useState('');
  const [search, setSearch] = useState<string | null>(null);
  const rows = query.data?.items.filter(({archived}) => !archived) ?? [];
  const term = search?.trim().toLocaleLowerCase() ?? '';
  const visibleRows = rows.filter((row) => matchesEquipmentSearch(row, term));
  const archiveFeature = (row: FeatureResponseBody) =>
    openConfirmDialog({
      text: t('entity.newVehicle.confirmations.archiveFeature', 'Archive equipment item {name}?', {
        name: row.name,
      }),
      onConfirm: () =>
        void archive({featureId: row.id, versionedWriteRequestBody: {version: row.version}})
          .unwrap()
          .then(() => void query.refetch())
          .catch((error: ApiError) => {
            const message = conflictMessage(error);
            if (!message) {
              handleCatalogueError(error);
              return;
            }
            const packageReferences = (packages.data?.items ?? [])
              .filter((item) => !item.archived && item.memberFeatureIds.includes(row.id))
              .map(
                (item) =>
                  `${i18n.t('entity.newVehicle.catalogue.package', {defaultValue: 'Package'})}: ${item.name}`
              );
            const ruleReferences = (rules.data?.items ?? [])
              .filter(
                (item) =>
                  !item.archived &&
                  (item.sourceFeatureId === row.id ||
                    item.targetFeatureIds.includes(row.id) ||
                    item.ifSelectedFeatureIds.includes(row.id) ||
                    item.appliesIf.allOfFeatureIds.includes(row.id))
              )
              .map(
                (item) =>
                  `${i18n.t('entity.newVehicle.catalogue.rule', {defaultValue: 'Rule'})}: ${item.note || item.id}`
              );
            setReferences([message, ...packageReferences, ...ruleReferences].join('\n'));
          }),
    });
  return (
    <VStack spacing={4}>
      <TabIntro>
        {t(
          'entity.newVehicle.catalogue.intro.featureLibrary',
          "Everything that can be fitted to this car. Create items in the library, then decide on each model year's Equipment by trim tab which trims include them as standard or sell them as options."
        )}
      </TabIntro>
      <Card
        title={t('entity.newVehicle.catalogue.featureLibrary', 'Equipment library')}
        actions={
          canWrite
            ? [
                {
                  type: 'button',
                  title: t('entity.newVehicle.catalogue.addFeature', 'Add equipment'),
                  leftIcon: 'content/add',
                  onClick: () => setEditing('new'),
                },
              ]
            : []
        }
      >
        <DataStatus
          isLoading={query.isLoading || bundle.isLoading}
          isError={query.isError || bundle.isError}
          isEmpty={!rows.length}
          emptyMessage={t('entity.newVehicle.catalogue.empty.features', 'No equipment yet')}
          emptySubheadline={t(
            'entity.newVehicle.catalogue.empty.featuresHint',
            'Add wheels, options and accessories, then place them on trims.'
          )}
          action={
            canWrite
              ? {
                  title: t('entity.newVehicle.catalogue.addFeature', 'Add equipment'),
                  leftIcon: 'content/add',
                  onClick: () => setEditing('new'),
                }
              : undefined
          }
        >
          <VStack spacing={4}>
            <Search
              value={search}
              onChange={setSearch}
              placeholder={t(
                'entity.newVehicle.authoring.searchEquipmentPlaceholder',
                'Search equipment…'
              )}
            />
            <DataStatus
              isEmpty={!visibleRows.length}
              emptyMessage={t('general.labels.noResultsFound', 'No results found')}
            >
              <Table
                columns={[
                  column(t('entity.newVehicle.catalogue.equipment', 'Equipment')),
                  column(t('entity.newVehicle.catalogue.howSold', 'How it is sold'), {
                    width: '30%',
                  }),
                  column(t('entity.newVehicle.catalogue.defaultPriceNet', 'Default price net'), {
                    width: '14%',
                    align: 'right',
                  }),
                  ...(canViewCost
                    ? [
                        column(t('entity.newVehicle.catalogue.costNet', 'Cost net'), {
                          width: '12%',
                          align: 'right',
                        }),
                      ]
                    : []),
                  actionsColumn(),
                ]}
              >
                {visibleRows.map((row) => (
                  <TableRow key={row.id} onRowClick={canWrite ? () => setEditing(row) : undefined}>
                    <Cell>
                      <VStack spacing={0}>
                        <Text size="small" alternative>
                          {row.name}
                        </Text>
                        <Text size="xSmall" color="secondary">
                          {joinParts([
                            getNewVehicleEnumLabel('featureCategory', row.category, t),
                            row.factoryCode ?? undefined,
                          ])}
                        </Text>
                      </VStack>
                    </Cell>
                    <Cell>
                      <HStack spacing={1} wrap>
                        <NewVehicleStatusFlag group="fitStage" value={row.fitStage} />
                        <Show when={!row.standaloneOrderable}>
                          <Flag
                            label={t('entity.newVehicle.rules.packageOnly', 'Package-only')}
                            colorScheme="neutral"
                            isSubtle
                          />
                        </Show>
                      </HStack>
                    </Cell>
                    <Cell align="right">
                      <PriceWithGross
                        amount={row.defaultPriceNet}
                        currency={currency}
                        vatRate={vatRate}
                      />
                    </Cell>
                    {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
                    {canViewCost ? (
                      <Cell align="right">
                        <MoneyText amount={row.costNet ?? '0.00'} currency={currency} />
                      </Cell>
                    ) : null}
                    <Cell>
                      <Show when={canWrite}>
                        <RowActions
                          actions={[
                            editRowAction(() => setEditing(row)),
                            archiveRowAction(() => archiveFeature(row)),
                          ]}
                        />
                      </Show>
                    </Cell>
                  </TableRow>
                ))}
              </Table>
            </DataStatus>
          </VStack>
        </DataStatus>
      </Card>
      <Show when={editing === 'new'}>
        <NewCatalogueEquipment
          modelYearId={modelYearId}
          canViewCost={canViewCost}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            void query.refetch();
            if (modelYearId) {
              void bundle.refetch();
            }
          }}
        />
      </Show>
      <Dialog
        isOpen={Boolean(editing && editing !== 'new')}
        onClose={() => setEditing(undefined)}
        withAdditionalFooter
        title={
          editing && editing !== 'new'
            ? t('entity.newVehicle.catalogue.editEquipmentNamed', 'Edit equipment · {name}', {
                name: editing.name,
              })
            : ''
        }
      >
        {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
        {editing && editing !== 'new' ? (
          <FeatureDialog
            item={editing}
            usedByModelYearId={
              bundle.data?.features.some(({id}) => id === editing.id) ? modelYearId : undefined
            }
            canViewCost={canViewCost}
            vatRate={vatRate}
            done={() => {
              setEditing(undefined);
              void query.refetch();
              if (modelYearId) {
                void bundle.refetch();
              }
            }}
          />
        ) : null}
      </Dialog>
      <Dialog
        isOpen={Boolean(references)}
        onClose={() => setReferences('')}
        title={t('entity.newVehicle.catalogue.archiveBlocked', 'Archive blocked')}
      >
        <Text>{references}</Text>
      </Dialog>
    </VStack>
  );
}

export function EquipmentByTrimTab(props: {
  modelYearId: string;
  currency: string;
  canWrite: boolean;
  canViewCost: boolean;
}) {
  const query = useListFeatureQuery({});
  const bundle = useGetAuthorConfiguratorBundleQuery({modelYearId: props.modelYearId});
  const trims = useListTrimQuery({});
  const vatRate = useCatalogueVatRate(props.modelYearId);
  const rows = query.data?.items.filter(({archived}) => !archived) ?? [];
  const yearTrims =
    trims.data?.items.filter((trim) => trim.modelYearId === props.modelYearId && !trim.archived) ??
    [];
  return (
    <DataStatus
      isLoading={query.isLoading || bundle.isLoading || trims.isLoading}
      // A failed refetch keeps the matrix and its unsaved drafts; only a failed first load errors.
      isError={
        (query.isError && isNil(query.data)) ||
        (bundle.isError && isNil(bundle.data)) ||
        (trims.isError && isNil(trims.data))
      }
    >
      <TrimEquipmentMatrix
        modelYearId={props.modelYearId}
        trims={yearTrims}
        features={rows}
        variants={(bundle.data?.variants ?? []).map(({id, orderCode, trimId}) => ({
          id,
          orderCode,
          trimId,
        }))}
        currency={props.currency}
        vatRate={vatRate}
        canWrite={props.canWrite}
        canViewCost={props.canViewCost}
      />
    </DataStatus>
  );
}

export function PackagesTab({
  modelYearId,
  currency,
  canWrite,
  canViewCost,
}: {
  modelYearId: string;
  currency: string;
  canWrite: boolean;
  canViewCost: boolean;
}) {
  const query = useListPackageQuery({});
  const features = useListFeatureQuery({});
  const trims = useListTrimQuery({});
  const [archive] = useArchivePackageMutation();
  const vatRate = useCatalogueVatRate(modelYearId);
  const [editing, setEditing] = useState<PackageResponseBody | 'new'>();
  const rows =
    query.data?.items.filter(
      (item) => item.modelYearId === modelYearId && !item.archived && item.status !== 'archived'
    ) ?? [];
  const featureRows = features.data?.items.filter((item) => !item.archived) ?? [];
  const trimRows =
    trims.data?.items.filter((item) => item.modelYearId === modelYearId && !item.archived) ?? [];
  const archivePackage = (row: PackageResponseBody) =>
    openConfirmDialog({
      text: t('entity.newVehicle.confirmations.archivePackage', 'Archive package {name}?', {
        name: row.name,
      }),
      onConfirm: () =>
        void archive({packageId: row.id, versionedWriteRequestBody: {version: row.version}})
          .unwrap()
          .then(() => void query.refetch())
          .catch(handleCatalogueError),
    });
  if (editing) {
    return (
      // The builder reads library taxonomy once on mount; an unloaded list would read as "orderable alone".
      <DataStatus isLoading={features.isLoading} isError={features.isError}>
        <PackageBuilder
          modelYearId={modelYearId}
          item={editing === 'new' ? undefined : editing}
          features={featureRows}
          trims={trimRows}
          currency={currency}
          canViewCost={canViewCost}
          onClose={() => setEditing(undefined)}
        />
      </DataStatus>
    );
  }
  return (
    <VStack spacing={3}>
      <TabIntro>
        {t(
          'entity.newVehicle.catalogue.intro.packages',
          'Bundles of equipment sold together at one price. Only active packages appear in the configurator.'
        )}
      </TabIntro>
      <Card
        title={t('entity.newVehicle.catalogue.packages', 'Packages')}
        actions={
          canWrite
            ? [
                {
                  type: 'button',
                  title: t('entity.newVehicle.catalogue.addPackage', 'Add package'),
                  leftIcon: 'content/add',
                  onClick: () => setEditing('new'),
                },
              ]
            : []
        }
      >
        <DataStatus
          isLoading={query.isLoading}
          isError={query.isError}
          isEmpty={!rows.length}
          emptyMessage={t('entity.newVehicle.authoring.noPackages', 'No packages yet')}
          emptySubheadline={t(
            'entity.newVehicle.catalogue.empty.packagesHint',
            'Group equipment that is usually bought together, like a winter or tech pack.'
          )}
          action={
            canWrite
              ? {
                  title: t('entity.newVehicle.catalogue.addPackage', 'Add package'),
                  leftIcon: 'content/add',
                  onClick: () => setEditing('new'),
                }
              : undefined
          }
        >
          <Table
            columns={[
              column(t('entity.newVehicle.catalogue.package', 'Package'), {width: '22%'}),
              column(t('entity.newVehicle.catalogue.includes', 'Includes')),
              column(t('entity.newVehicle.catalogue.offeredOn', 'Offered on'), {width: '18%'}),
              column(t('entity.newVehicle.catalogue.priceNet', 'Price net'), {
                width: '12%',
                align: 'right',
              }),
              ...(canViewCost
                ? [
                    column(t('entity.newVehicle.catalogue.costNet', 'Cost net'), {
                      width: '12%',
                      align: 'right',
                    }),
                  ]
                : []),
              column(t('entity.newVehicle.catalogue.status', 'Status'), {width: '10%'}),
              actionsColumn(),
            ]}
          >
            {rows.map((row) => (
              <TableRow key={row.id} onRowClick={canWrite ? () => setEditing(row) : undefined}>
                <Cell>
                  <VStack spacing={0}>
                    <Text size="small" alternative>
                      {row.name}
                    </Text>
                    <Text size="xSmall" color="secondary">
                      {row.codes[0]}
                    </Text>
                  </VStack>
                </Cell>
                <Cell>
                  <HStack spacing={1} wrap>
                    {row.memberFeatureIds.map((id) => (
                      <Flag
                        key={id}
                        label={featureRows.find((item) => item.id === id)?.name ?? id}
                        colorScheme="neutral"
                        isSubtle
                      />
                    ))}
                  </HStack>
                </Cell>
                <Cell>
                  {row.applicableTrimIds?.length
                    ? row.applicableTrimIds
                        .map((id) => trimRows.find((trim) => trim.id === id)?.name ?? id)
                        .join(', ')
                    : t('entity.newVehicle.catalogue.allTrims', 'All trims')}
                </Cell>
                <Cell align="right">
                  <PriceWithGross amount={row.priceNet} currency={currency} vatRate={vatRate} />
                </Cell>
                {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
                {canViewCost ? (
                  <Cell align="right">
                    <MoneyText amount={row.costNet ?? '0.00'} currency={currency} />
                  </Cell>
                ) : null}
                <Cell>
                  <NewVehicleStatusFlag group="packageStatus" value={row.status ?? 'draft'} />
                </Cell>
                <Cell>
                  <Show when={canWrite}>
                    <RowActions
                      actions={[
                        editRowAction(() => setEditing(row)),
                        archiveRowAction(() => archivePackage(row)),
                      ]}
                    />
                  </Show>
                </Cell>
              </TableRow>
            ))}
          </Table>
        </DataStatus>
      </Card>
    </VStack>
  );
}

function RuleTypeSegment(props: {control: Control<RuleValues>}) {
  return (
    <Controller
      control={props.control}
      name="type"
      render={({field}) => (
        <Segment
          label={t('entity.newVehicle.catalogue.ruleType', 'Rule type')}
          value={field.value}
          options={['requires', 'excludes', 'autoAdd'].map((value) => ({
            value,
            label: getNewVehicleEnumLabel('ruleType', value, t),
          }))}
          onChange={field.onChange}
        />
      )}
    />
  );
}
function RulePreview(props: {control: Control<RuleValues>; features: FeatureResponseBody[]}) {
  const values = useWatch({control: props.control});
  const parsed = ruleSchema.safeParse(values);
  if (!parsed.success) {
    return null;
  }
  return (
    <>
      <Alert variant="info" message={ruleSentence(parsed.data, props.features)} />
      <Show when={parsed.data.type === 'excludes'}>
        <Text size="small" color="secondary">
          {t(
            'entity.newVehicle.rules.excludesSymmetric',
            'Excludes rules apply in both directions.'
          )}
        </Text>
      </Show>
    </>
  );
}

function RuleDialog({
  modelYearId,
  item,
  features,
  done,
}: {
  modelYearId: string;
  item?: FeatureRuleResponseBody;
  features: FeatureResponseBody[];
  done: () => void;
}) {
  const [create] = useCreateFeatureRuleMutation();
  const [update] = useUpdateFeatureRuleMutation();
  const initialValues: RuleValues = {
    type: item?.type ?? 'requires',
    sourceFeatureId: item?.sourceFeatureId ?? '',
    targetFeatureIds: item?.targetFeatureIds ?? [],
    ifSelectedFeatureIds: item?.ifSelectedFeatureIds ?? [],
    note: item?.note ?? '',
  };
  const submit = async (values: RuleValues) => {
    const body = {
      modelYearId,
      ...values,
      note: values.note || undefined,
      appliesIf: item?.appliesIf ?? emptyCondition,
      version: item?.version ?? 1,
    };
    await (
      item
        ? update({featureRuleId: item.id, featureRuleWriteRequestBody: body})
        : create({featureRuleWriteRequestBody: body})
    )
      .unwrap()
      .then(done)
      .catch(handleCatalogueError);
  };
  return (
    <Form<RuleValues>
      experimentalZodSchema={ruleSchema}
      defaultValues={initialValues}
      onSubmit={submit}
    >
      {(control) => (
        <VStack spacing={3}>
          <RuleTypeSegment control={control} />
          <FormField
            control={control}
            name="sourceFeatureId"
            type="choice"
            menuInPortal
            label={t('entity.newVehicle.catalogue.source', 'Source')}
            options={features.map(({id, name}) => ({value: id, label: name}))}
          />
          <FormField
            control={control}
            name="targetFeatureIds"
            type="multiChoice"
            menuInPortal
            label={t('entity.newVehicle.catalogue.targets', 'Targets')}
            options={features.map(({id, name}) => ({value: id, label: name}))}
          />
          <FormField
            control={control}
            name="ifSelectedFeatureIds"
            type="multiChoice"
            menuInPortal
            label={t('entity.newVehicle.catalogue.ifSelected', 'If selected')}
            options={features.map(({id, name}) => ({value: id, label: name}))}
          />
          <RulePreview control={control} features={features} />
          <FormField
            control={control}
            name="note"
            type="textarea"
            label={t('general.labels.note', 'Note')}
          />
          <DialogFooter>
            <HStack justify="flex-end">
              <FormButton
                control={control}
                type="button"
                title={t('general.actions.save', 'Save')}
                onClick={() => void control.submitForm()}
              />
            </HStack>
          </DialogFooter>
        </VStack>
      )}
    </Form>
  );
}
export function RulesTab({modelYearId, canWrite}: {modelYearId: string; canWrite: boolean}) {
  const query = useListFeatureRuleQuery({});
  const features = useListFeatureQuery({});
  const [remove] = useDeleteFeatureRuleMutation();
  const [editing, setEditing] = useState<FeatureRuleResponseBody | 'new'>();
  const [deleting, setDeleting] = useState<FeatureRuleResponseBody>();
  const featureRows = features.data?.items.filter((item) => !item.archived) ?? [];
  const rows =
    query.data?.items.filter((item) => item.modelYearId === modelYearId && !item.archived) ?? [];
  return (
    <VStack spacing={3}>
      <TabIntro>
        {t(
          'entity.newVehicle.catalogue.intro.rules',
          'Rules stop impossible orders: equipment that needs other equipment, items that cannot be combined, and items that are added automatically.'
        )}
      </TabIntro>
      <Card
        title={t('entity.newVehicle.catalogue.rules', 'Rules')}
        actions={
          canWrite
            ? [
                {
                  type: 'button',
                  title: t('entity.newVehicle.catalogue.addRule', 'Add rule'),
                  leftIcon: 'content/add',
                  onClick: () => setEditing('new'),
                },
              ]
            : []
        }
      >
        <DataStatus
          isLoading={query.isLoading}
          isError={query.isError}
          isEmpty={!rows.length}
          emptyMessage={t('entity.newVehicle.catalogue.empty.rules', 'No rules yet')}
          emptySubheadline={t(
            'entity.newVehicle.catalogue.empty.rulesHint',
            'Without rules, customers can combine any equipment freely.'
          )}
          action={
            canWrite
              ? {
                  title: t('entity.newVehicle.catalogue.addRule', 'Add rule'),
                  leftIcon: 'content/add',
                  onClick: () => setEditing('new'),
                }
              : undefined
          }
        >
          <Table
            columns={[
              column(t('entity.newVehicle.catalogue.rule', 'Rule')),
              column(t('entity.newVehicle.catalogue.type', 'Type'), {width: '16%'}),
              actionsColumn(),
            ]}
          >
            {rows.map((row) => (
              <TableRow key={row.id} onRowClick={canWrite ? () => setEditing(row) : undefined}>
                <Cell>
                  <VStack spacing={0}>
                    <Text size="small">
                      {ruleSentence(
                        {
                          type: row.type,
                          sourceFeatureId: row.sourceFeatureId,
                          targetFeatureIds: row.targetFeatureIds,
                          ifSelectedFeatureIds: row.ifSelectedFeatureIds,
                          note: row.note ?? '',
                        },
                        featureRows
                      )}
                    </Text>
                    <Show when={Boolean(row.note)}>
                      <Text size="xSmall" color="secondary">
                        {row.note}
                      </Text>
                    </Show>
                  </VStack>
                </Cell>
                <Cell>
                  <NewVehicleStatusFlag group="ruleType" value={row.type} />
                </Cell>
                <Cell>
                  <Show when={canWrite}>
                    <RowActions
                      actions={[
                        editRowAction(() => setEditing(row)),
                        {
                          icon: 'action/delete',
                          label: t('general.actions.delete', 'Delete'),
                          onClick: () => setDeleting(row),
                        },
                      ]}
                    />
                  </Show>
                </Cell>
              </TableRow>
            ))}
          </Table>
        </DataStatus>
        <Dialog
          isOpen={Boolean(deleting)}
          onClose={() => setDeleting(undefined)}
          title={t('entity.newVehicle.catalogue.deleteRuleConfirm', 'Delete this rule?')}
          buttons={[
            {
              title: t('general.actions.cancel', 'Cancel'),
              variant: 'secondary',
              onClick: () => setDeleting(undefined),
            },
            {
              title: t('general.actions.delete', 'Delete'),
              onClick: () => {
                if (!deleting) {
                  return;
                }
                void remove({
                  featureRuleId: deleting.id,
                  versionedWriteRequestBody: {version: deleting.version},
                })
                  .unwrap()
                  .then(() => {
                    setDeleting(undefined);
                    void query.refetch();
                  })
                  .catch(handleCatalogueError);
              },
            },
          ]}
        >
          <Text>
            {t('entity.newVehicle.catalogue.deleteRuleWarning', 'This cannot be undone.')}
          </Text>
        </Dialog>
        <Dialog
          isOpen={Boolean(editing)}
          onClose={() => setEditing(undefined)}
          withAdditionalFooter
          title={
            editing === 'new'
              ? t('entity.newVehicle.catalogue.addRule', 'Add rule')
              : t('entity.newVehicle.catalogue.editRule', 'Edit rule')
          }
        >
          {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
          {editing ? (
            <RuleDialog
              modelYearId={modelYearId}
              item={editing === 'new' ? undefined : editing}
              features={featureRows}
              done={() => {
                setEditing(undefined);
                void query.refetch();
              }}
            />
          ) : null}
        </Dialog>
      </Card>
    </VStack>
  );
}
